import { Vector2 } from "../../common/vector2.js";
import { NineSliceDictionary } from "../../image_loader/nine_slicer/nine_slice_dictionary.js";
import { BackButtonHandler } from "../../navigation/back_button_handler.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { ResourceManager } from "../../resource_loader/resource_manager.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { DevToDev } from "../../plugins/dev_to_dev_plugin.js";
import { releaseHiddenCanvas, restoreReleasedCanvas } from "../../utils/canvas_memory.js";
import { CrashBreadcrumbs } from "../../crash/breadcrumbs.js";

export class DialogManager
{
  public static ctx: CanvasRenderingContext2D;

  private static dialog_stack: AbstractDialog[] = [];
  private static canvas: HTMLCanvasElement;
  private static events_controller: BasicEventsController;
  private static width:number;
  private static height:number;
  private static overlay: HTMLElement;
  public static ns_dictionary = NineSliceDictionary.values;

  private static is_down_outside_of_dialog:boolean = false;
  private static is_route_transition_active:boolean = false; 
  private static pending_route_dialog: AbstractDialog | null = null;
  private static pending_close_after_transition:boolean = false;
  private static interaction_blocked_until_ms:number = 0;
  private static overlay_is_visible:boolean = false;
  private static last_open_at = 0;
  private static last_rewarded_cooldown_debug_render_at = 0;
  private static readonly ABOVE_OVERLAYS_BACKDROP_Z = "127";
  private static readonly ABOVE_OVERLAYS_CANVAS_Z = "128";

  public static init( ctx: CanvasRenderingContext2D, width:number, height:number ): void
  {
    this.ctx = ctx;
    this.canvas = ctx.canvas;
    this.width = width;
    this.height = height;
    this.overlay = document.getElementById( 'dialogOverlay' ) as HTMLElement

    this.events_controller = new BasicEventsController( this.canvas );
    this.events_controller.init(
      ( p:Vector2 ) => this.handleDown( p ),
      ( p:Vector2 ) => this.handleUp  ( p ),
      ( p:Vector2 ) => this.handleMove( p )
    );
  }

  public static setScreenSize( width: number, height: number ): void
  {
    this.width = width;
    this.height = height;
  }

  public static update( now:number ):void
  {
    if ( this.dialog_stack.length == 0 )
      return;

    const top_dialog = this.topDialog();
    top_dialog.update( now );

    if ( top_dialog.isCloseAnimationFinished( now ) )
    {
      this.finalizeTopDialogClose();
    }
  }

  public static render():void
  {
    if ( this.dialog_stack.length == 0 )
      return;

    const top_dialog = this.topDialog();
    if ( !top_dialog.shouldBeRendered() )
    {
      return;
    }

    const top_dialog_name = top_dialog.name();
    const debug_rewarded_dialog = top_dialog_name === 'BuyBoosterDialog'
      || top_dialog_name === 'MoreLivesDialog';
    const debug_now = Date.now();
    if ( debug_rewarded_dialog
      && debug_now - this.last_rewarded_cooldown_debug_render_at >= 750 )
    {
      this.last_rewarded_cooldown_debug_render_at = debug_now;
      console.log( '[RewardedCooldownUI][DialogManager] clear and render', {
        top_dialog: top_dialog_name,
        stack: this.dialog_stack.map( dialog => dialog.name() ),
        overlay_is_visible: this.overlay_is_visible,
        canvas_display: this.canvas.style.display,
        canvas_size: `${this.width}x${this.height}`,
        dialog_children: top_dialog.getChildren().map( child => ( {
          name: child.name(),
          should_render: child.shouldBeRendered(),
        } ) ),
      } );
    }

    this.ctx.clearRect( 0, 0, this.width, this.height );

    const bg_img_id = top_dialog.getBackgroundImageId();
    if ( bg_img_id !== null )
    {
      const img = ImagesHolder.getImage( bg_img_id );
      if ( img instanceof ImageBitmap )
      {
        const scale = Math.max( this.width / img.width, this.height / img.height );
        const draw_w = img.width * scale;
        const draw_h = img.height * scale;
        const draw_x = ( this.width - draw_w ) / 2;
        const draw_y = ( this.height - draw_h ) / 2;
        this.ctx.drawImage( img, draw_x, draw_y, draw_w, draw_h );
      }
    }

    top_dialog.render();
  }

  private static handleDown( p:Vector2 ): void
  {
    if ( this.isInteractionBlocked() )
      return;

    if ( this.dialog_stack.length === 0 )
      return;

    if ( this.isInsideOpenedDialog( p ) )
    {
      const top_dialog = this.topDialog();
      top_dialog.onTouchStart( p, performance.now() );
      return;
    }

    this.is_down_outside_of_dialog = true;
  }

  private static handleUp( p:Vector2 ): void
  {
    if ( this.isInteractionBlocked() )
      return;

    if ( this.dialog_stack.length === 0 )
      return;

    const top_dialog = this.topDialog();
    top_dialog.onTouchEnd( p, performance.now() );

    if ( this.isInsideOpenedDialog( p ) )
    {
      this.is_down_outside_of_dialog = false;
      return;
    }

    // Клик за пределами диалога — закрываем его
    if ( !this.is_down_outside_of_dialog )
      return;

    this.is_down_outside_of_dialog = false;
    if ( this.topDialog().getCloseOnBackgroundClick() )
      this.closeDialog( 'background_tap' );
  }

  private static handleMove( p:Vector2 ): void
  {
    if ( this.isInteractionBlocked() )
      return;

    if ( this.dialog_stack.length === 0 )
      return;

    if ( this.isInsideOpenedDialog( p ) )
    {
      const top_dialog = this.topDialog();
      top_dialog.onTouchMove( p, performance.now() );
      return;
    }
  }

  private static isInsideOpenedDialog( p:Vector2 ): boolean
  {
    if ( this.dialog_stack.length === 0 )
      return false;

    //console.log( `isInsideOpenedDialog( p ${p}` );

    const top_dialog = this.topDialog();
    return top_dialog.containsPointGlobal( p );
  }

  public static openDialog( dialog: AbstractDialog ): void
  {
    this.openDialogInternal( dialog, true, true );
  }

  // Overlays sit above the dialog layer, so a dialog opened from an overlay
  // lifts the dialog layer over them until the dialog stack is empty again.
  public static openDialogAboveOverlays( dialog: AbstractDialog ): void
  {
    this.overlay.style.zIndex = DialogManager.ABOVE_OVERLAYS_BACKDROP_Z;
    this.canvas.style.zIndex = DialogManager.ABOVE_OVERLAYS_CANVAS_Z;
    this.openDialog( dialog );
  }

  public static routeToDialog( dialog: AbstractDialog ): void
  {
    if ( this.is_route_transition_active )
    {
      // Keep only the latest requested destination to avoid transition queue growth.
      this.pending_route_dialog = dialog;
      return;
    }

    if ( !this.isAnyDialogOpened() )
    {
      this.openDialog( dialog );
      return;
    }

    const top_dialog = this.topDialog();
    if ( top_dialog.isClosing() )
    {
      this.pending_route_dialog = dialog;
      this.is_route_transition_active = true;
      return;
    }

    this.pending_route_dialog = dialog;
    this.is_route_transition_active = true;
    this.is_down_outside_of_dialog = false;
    this.interaction_blocked_until_ms = 0;

    // Route transition uses softer scale, then opens next dialog without overlay flicker.
    top_dialog.onDialogCloseRequested( 'route_transition' );
    top_dialog.startCloseAnimation( 0.92, 112 );
  }

  private static openDialogInternal(
    dialog: AbstractDialog,
    should_animate_overlay: boolean,
    should_animate_dialog: boolean
  ): void
  {
    console.log( `[DialogManager] Opening dialog: ${dialog.name?.() ?? 'unknown'}, stack size before: ${this.dialog_stack.length}` );
    this.dialog_stack.push( dialog );
    CrashBreadcrumbs.add( 'dialog', `open ${dialog.name?.() ?? 'unknown'}` );
    this.last_open_at = performance.now();
    if ( should_animate_dialog )
      dialog.onDialogOpened();
    else
    {
      this.cancelCanvasAnim();
      this.canvas.style.opacity = "";
    }
    dialog.onDialogShown();
    this.show( should_animate_overlay );
    console.log( `[DialogManager] Dialog opened, stack size: ${this.dialog_stack.length}, canvas display: ${this.canvas.style.display}` );
    BackButtonHandler.getInstance().updateBackButtonVisibility();
    // setTimeout( () => { audioController.playSound( AudioConstants.UI_TOOLTIP_OPEN, false ); }, 100 );
    try { void DevToDev.sendEvent(`dialog_open_${dialog.name?.() ?? 'unknown'}`); } catch {}
    audioController.playSound( AudioClips.UI_CLICK );
    // try { void DevToDev.sendEvent(`dialog_open_${dialog.name?.() ?? 'unknown'}`); } catch {}
  }

/*  
  public static openMultizoneDialog( dialog: MultiZoneDialog ): void
  {
    this.dialog_stack.push( dialog );
    this.show();
  }
*/
  public static closeAfterTransition(): void
  {
    if ( !this.is_route_transition_active )
    {
      this.closeDialog();
      return;
    }
    this.pending_close_after_transition = true;
  }

  public static closeAllDialogs(): void
  {
    this.pending_route_dialog = null;
    this.pending_close_after_transition = false;
    this.is_route_transition_active = false;
    this.interaction_blocked_until_ms = 0;
    while ( this.isAnyDialogOpened() )
    {
      const closed = this.dialog_stack.pop();
      closed?.onDialogCloseRequested( 'close_all' );
      this.callOnClose( closed );
      try { void DevToDev.sendEvent(`dialog_close_${closed?.name?.() ?? 'unknown'}`); } catch {}
    }

    this.ctx.clearRect( 0, 0, this.width, this.height );
    this.hide(); 
    BackButtonHandler.getInstance().updateBackButtonVisibility();
  }

  public static closeDialog( reason: string = 'programmatic' ): void
  {
    if ( this.is_route_transition_active )
      return;
 
    if ( !this.isAnyDialogOpened() ) 
      return;

    const top_dialog = this.topDialog();
    if ( top_dialog.isClosing() )
    {
      return;
    }

    top_dialog.onDialogCloseRequested( reason );
    void audioController.playSound( AudioClips.UI_CLICK );

    const is_last_dialog = this.dialog_stack.length === 1;
    if ( !is_last_dialog )
      this.is_route_transition_active = true;

    top_dialog.startCloseAnimation();
    if ( is_last_dialog )
    {
      this.overlay.style.opacity = "0";
    }
  }

  private static finalizeTopDialogClose(): void
  {
    if ( !this.isAnyDialogOpened() )
      return;

    const closed = this.dialog_stack.pop();
    CrashBreadcrumbs.add( 'dialog', `close ${closed?.name?.() ?? 'unknown'}` );
    console.log( `[DialogManager] Closing dialog: ${closed?.name?.() ?? 'unknown'}, stack size after: ${this.dialog_stack.length}` );
    this.callOnClose( closed );
    try { void DevToDev.sendEvent(`dialog_close_${closed?.name?.() ?? 'unknown'}`); } catch {}
    if ( this.pending_route_dialog )
    {
      const next_dialog = this.pending_route_dialog;
      this.pending_route_dialog = null;
      this.openDialogInternal( next_dialog, false, false );
      this.is_route_transition_active = false;
      return;
    }

    if ( this.pending_close_after_transition )
    {
      this.pending_close_after_transition = false;
      this.is_route_transition_active = false;
      if ( this.isAnyDialogOpened() )
        this.closeDialog();
      return;
    }

    if ( !this.isAnyDialogOpened() )
    {
      this.ctx.clearRect( 0, 0, this.width, this.height );
      this.canvas.style.display = "none";
      releaseHiddenCanvas( this.canvas );
      this.overlay_is_visible = false;
      // closeDialog() started the CSS opacity transition (1→0, 144ms).
      // Delay display:none by one frame so the final opacity-0 state is painted first.
      requestAnimationFrame( () => {
        if ( !this.overlay_is_visible )
          this.overlay.style.display = "none";
      } );
      BackButtonHandler.getInstance().updateBackButtonVisibility();
      this.is_route_transition_active = false;
      return;
    }
    else
    {
      const revealed_dialog = this.topDialog();
      revealed_dialog.playReturnToTopAnimation();
      this.overlay.style.opacity = "1";
      this.interaction_blocked_until_ms = performance.now() + 112;
    }

    BackButtonHandler.getInstance().updateBackButtonVisibility();
    this.is_route_transition_active = false;
  }

  private static canvas_anim: Animation | null = null;
  private static readonly EASE_OUT_QUAD = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  public static useCssAnimations( dialog: AbstractDialog ): boolean
  {
    return dialog.getBackgroundImageId() === null
      && typeof this.canvas?.animate === "function"
      && localStorage.getItem( "perf_diag_dialog_canvas_anim" ) !== "1";
  }

  private static cancelCanvasAnim(): void
  {
    if ( !this.canvas_anim )
      return;

    this.canvas_anim.cancel();
    this.canvas_anim = null;
  }

  private static runCanvasAnim(
    dialog: AbstractDialog,
    keyframes: Keyframe[],
    duration_ms: number,
    cancel_on_finish: boolean
  ): void
  {
    this.cancelCanvasAnim();

    const center = dialog.getCenterGlobal();
    this.canvas.style.transformOrigin = `${center.x}px ${center.y}px`;

    const anim = this.canvas.animate( keyframes, {
      duration: duration_ms,
      fill: "both",
    } );
    this.canvas_anim = anim;

    if ( cancel_on_finish )
    {
      anim.onfinish = () =>
      {
        if ( this.canvas_anim === anim )
        {
          anim.cancel();
          this.canvas_anim = null;
        }
      };
    }
  }

  public static animateAppearCss(
    dialog: AbstractDialog,
    from_scale: number,
    overshoot_scale: number,
    final_scale: number,
    scale_up_duration: number,
    settle_duration: number
  ): void
  {
    // The first GPU raster of a freshly drawn dialog can stall the main thread
    // for hundreds of ms (cold per dialog type). Keep the canvas invisible for
    // one presented frame so the stall happens behind the dim overlay, then run
    // the full appear animation from its start.
    this.cancelCanvasAnim();
    this.canvas.style.opacity = "0";

    requestAnimationFrame( () => requestAnimationFrame( () =>
    {
      if ( this.topDialogSafe() !== dialog || dialog.isClosing() )
      {
        this.canvas.style.opacity = "";
        return;
      }

      this.canvas.style.opacity = "";
      const total = scale_up_duration + settle_duration;
      this.runCanvasAnim( dialog, [
        { transform: `scale(${from_scale})`, opacity: 0, offset: 0, easing: this.EASE_OUT_QUAD },
        { transform: `scale(${overshoot_scale})`, opacity: 1, offset: scale_up_duration / total, easing: this.EASE_OUT_QUAD },
        { transform: `scale(${final_scale})`, opacity: 1, offset: 1 },
      ], total, true );
      dialog.onCssAppearStarted();
    } ) );
  }

  private static topDialogSafe(): AbstractDialog | null
  {
    return this.dialog_stack.length > 0 ? this.dialog_stack[this.dialog_stack.length - 1] : null;
  }

  public static animateCloseCss(
    dialog: AbstractDialog,
    to_scale: number,
    duration_ms: number
  ): void
  {
    this.runCanvasAnim( dialog, [
      { transform: "scale(1)", opacity: 1, easing: this.EASE_OUT_QUAD },
      { transform: `scale(${to_scale})`, opacity: 0 },
    ], duration_ms, false );
  }

  public static animateReturnToTopCss(
    dialog: AbstractDialog,
    from_scale: number,
    final_scale: number,
    duration_ms: number
  ): void
  {
    this.runCanvasAnim( dialog, [
      { transform: `scale(${from_scale})`, opacity: 0, easing: this.EASE_OUT_QUAD },
      { transform: `scale(${final_scale})`, opacity: 1 },
    ], duration_ms, true );
  }

  // The first GPU raster of each dialog type stalls the main thread for
  // 300-500ms on weak devices (pipeline/texture warmup inside the raster
  // process). Rendering each hot dialog once into the hidden dialog canvas
  // during menu idle pays that cost off-screen.
  public static async prewarmDialogs( factories: Array<() => AbstractDialog> ): Promise<void>
  {
    if ( !this.overlay_is_visible )
    {
      this.overlay.style.opacity = "0.01";
      this.overlay.style.display = "block";
      await new Promise<void>( r => requestAnimationFrame( () => requestAnimationFrame( () => r() ) ) );
      if ( !this.overlay_is_visible )
      {
        this.overlay.style.display = "none";
        this.overlay.style.opacity = "0";
      }
    }

    for ( const factory of factories )
    {
      await ResourceManager.pacingDelay();

      // A real dialog owns the canvas now (or might grab it mid-item): stop
      // for good instead of racing it — a prewarm render would overwrite the
      // visible dialog and leave the canvas at prewarm opacity.
      if ( this.isAnyDialogOpened() )
        return;

      try
      {
        const dialog = factory();
        if ( this.isAnyDialogOpened() )
          return;
        restoreReleasedCanvas( this.canvas );
        this.canvas.style.opacity = "0.01";
        this.canvas.style.display = "block";
        dialog.render();
        await new Promise<void>( r => requestAnimationFrame( () => requestAnimationFrame( () => r() ) ) );
        this.callOnClose( dialog );
        if ( this.isAnyDialogOpened() )
          return;
        this.ctx.clearRect( 0, 0, this.width, this.height );
      }
      catch ( e )
      {
        console.warn( "[DialogManager] prewarm failed", e );
      }
      finally
      {
        this.canvas.style.opacity = "";
        if ( !this.isAnyDialogOpened() )
          this.canvas.style.display = "none";
      }
    }

    if ( !this.isAnyDialogOpened() )
      releaseHiddenCanvas( this.canvas );
  }

  private static callOnClose( closed: AbstractDialog | undefined ): void
  {
    try { closed?.onDialogWillClose(); }
    catch ( e ) { console.warn( "DialogManager: onDialogWillClose failed", e ); }

    try { closed?.cleanup(); }
    catch ( e ) { console.warn( "DialogManager: cleanup failed", e ); }
  }

  public static isAnyDialogOpened() { return this.dialog_stack.length > 0; }

  public static msSinceLastOpen(): number { return performance.now() - this.last_open_at; }

  public static getTopDialog(): AbstractDialog | null
  {
    return this.dialog_stack.length > 0 ? this.dialog_stack[this.dialog_stack.length - 1] : null;
  }

  public static getWidth() { return this.width; }

  public static getHeight() { return this.height; }

  private static isInteractionBlocked(): boolean
  {
    return this.is_route_transition_active
      || performance.now() < this.interaction_blocked_until_ms
      || ( this.dialog_stack.length > 0 && this.topDialog().isClosing() );
  }

  private static topDialog():AbstractDialog
  {
    if ( this.dialog_stack.length == 0 )
      throw new Error('topDialog(): No dialogs are open');

    return this.dialog_stack[this.dialog_stack.length - 1];
  }

  private static hide(): void
  {
    this.cancelCanvasAnim();
    this.canvas.style.opacity = "";
    this.overlay_is_visible = false;
    this.canvas.style.display = "none";
    releaseHiddenCanvas( this.canvas );
    this.overlay.style.opacity = "0";
    this.overlay.style.display = "none";
    this.overlay.style.zIndex = "";
    this.canvas.style.zIndex = "";
  }

  private static show( should_animate_overlay: boolean = true ): void
  {
    const was_visible = this.overlay_is_visible;
    this.overlay_is_visible = true;
    if ( this.canvas.style.opacity === "0.01" )
      this.canvas.style.opacity = "";
    restoreReleasedCanvas( this.canvas );
    this.canvas.style.display = "block";
    this.overlay.style.display = "block";

    // If overlay was already visible, or route transition — just snap to 1 without restarting the transition.
    if ( !should_animate_overlay || was_visible )
    {
      this.overlay.style.opacity = "1";
      return;
    }

    this.overlay.style.opacity = "0";
    requestAnimationFrame( () => { this.overlay.style.opacity = "1"; } );
  }
}
